<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { eqsApi, type IssueEditRights } from '@/api/eqs';
import { useSession } from '@/auth/session';
import ExportDataButton from '@/components/ExportDataButton.vue';
import Issue8dBoard from '@/components/Issue8dBoard.vue';
import ProjectIssueList from '@/components/ProjectIssueList.vue';
import PageState from '@/components/PageState.vue';
import ProjectCreationWizard from '@/components/ProjectCreationWizard.vue';
import RecordListDashboard from '@/components/RecordListDashboard.vue';
/*
  原来这里挂 ChangeDrawerAssist（下一步提示 / 生成执行清单 / 催办措施）。
  0824 三样东西全并进 ChangeStepBar：「下一步」变成每一步自带的 hint，
  生成和催办落到第 4 步——清单本来就是那一步的任务。组件已删。
*/
import ChangeLedgerTable from '@/components/ChangeLedgerTable.vue';
import ChangeStepBar from '@/components/ChangeStepBar.vue';
import IssueDrawerAssist from '@/components/IssueDrawerAssist.vue';
import OnlyOfficeStage from '@/components/OnlyOfficeStage.vue';
import RecordDrawer from '@/components/RecordDrawer.vue';
import type { DrawerChildList, DrawerStatusAction } from '@/components/record-drawer';
import RecordFormDialog from '@/components/RecordFormDialog.vue';
import StatusChip from '@/components/StatusChip.vue';
import { moduleByKey, projectPathForModule } from '@/data/modules';
import { notify } from '@/services/notice';
import type { ModuleCatalogItem, ModuleField, ModuleRecord, Project, ProjectStatus } from '@/types/domain';
import { errorMessage, formatCell, formatDate } from '@/utils/format';
import { projectStage, projectStageLabel } from '@/utils/portfolio';
import { buildProjectWritePayload, projectStatusActions } from '@/utils/project-workflow';
import { buildExportFilename } from '@/utils/csv-export';
import { recordExportColumns, recordExportRows } from '@/utils/data-export';
import { matchesDrill as matchesDashboardDrill, type DashboardLabels } from '@/utils/module-dashboard';
import { confirmAction } from '@/services/confirm';
import { useFormOptions } from '@/composables/useFormOptions';
import { useIssueInlineUpdate } from '@/composables/useIssueInlineUpdate';
import { changeDialogTitle, createChangeViaStepForm } from '@/composables/useChangeChecklist';

const props = defineProps<{ moduleKey: string }>();
const { fieldOptions, loadFormOptions, withStandardOptions } = useFormOptions();
const router = useRouter();
const session = useSession();
const definition = computed(() => moduleByKey(props.moduleKey));
const records = ref<ModuleRecord[]>([]);
const catalogItem = ref<ModuleCatalogItem | null>(null);
const loading = ref(true);
const listError = ref('');
const summaryError = ref('');
const query = ref('');
const selected = ref<ModuleRecord | null>(null);
const form = ref<Record<string, string>>({});
const saving = ref(false);
const recordLoading = ref(false);
const importInput = ref<HTMLInputElement | null>(null);
const importLoading = ref(false);
const importConfirming = ref(false);
const importPreview = ref<{
  fileName: string;
  total: number;
  creates: number;
  updates: number;
  valid: number;
  invalid: number;
  rows: Array<{
    row: number;
    recordId?: number;
    action: 'create' | 'update';
    values: Record<string, unknown>;
    errors: string[];
  }>;
} | null>(null);
const creatingProject = ref(false);
const recordDialogOpen = ref(false);
const recordDialog = ref<InstanceType<typeof RecordFormDialog> | null>(null);
/* 抽屉骨架（附件/历史/子条目/状态/在线编辑）和全屏编辑台都在共享组件里 */
const drawer = ref<InstanceType<typeof RecordDrawer> | null>(null);
const editorStage = ref<InstanceType<typeof OnlyOfficeStage> | null>(null);

/** 5Why / 历史案例要读写抽屉里的活表单，包成两个回调交给 IssueDrawerAssist */
function drawerValues(): Record<string, string> {
  return recordDialog.value?.getValues() ?? {};
}
function patchDrawerValues(patch: Record<string, string>): void {
  recordDialog.value?.setValues(patch);
}

/** 问题模块的两个视图：混合清单 / 只看带 8D 步骤的问题 */
const issueView = ref<'list' | '8d'>('list');

/**
 * 从问题清单点「转 8D / 查看 8D」之后，把人带到 8D 视图并定位到那一条。
 *
 * 原来转完只弹一句提示，人不知道该去哪看、去哪改——「整个流程还不是很通」
 * （2026-08-18 Tony）。转完就切视图，是这条链上唯一缺的一步。
 */
function goToEightD(item: ModuleRecord): void {
  issueView.value = '8d';
  eightDFocusId.value = Number(item.id);
}
/** 高亮刚转过来的那条，滚动定位交给 8D 面板 */
const eightDFocusId = ref<number | null>(null);

/*
  全局问题清单换成和项目页同一个组件（ProjectIssueList）。
  原来两套实现：全局是通用表格（没有附件/进展/催办/8D/行内编辑），
  项目页是按试用反馈打磨过的那套——同一份数据两副面孔，
  人在两边看到的列都对不上（Tony 2026-08-18）。统一后差异只剩「关联项目」一列。
*/
const projectLabels = computed<Record<string, string>>(() =>
  Object.fromEntries(projectOptions.value.map((option) => [String(option.value), option.label])));

async function quickCreateIssueGlobal(draft: Record<string, string>): Promise<void> {
  const payload: Record<string, unknown> = {
    title: draft.title,
    normalized: {
      description: draft.title,
      foundAt: new Date().toISOString().slice(0, 10),
      ...(draft.severityGrade ? { severityGrade: draft.severityGrade } : {}),
    },
  };
  if (draft.ownerName) payload.ownerName = draft.ownerName;
  if (draft.dueAt) payload.dueAt = new Date(`${draft.dueAt}T23:59:59+08:00`).toISOString();
  try {
    await eqsApi.createModuleRecord('issues', 'issues', payload);
    notify('success', '已记下这条问题', '标题带项目代号会自动挂到项目；分类和责任细节点行内补齐。');
    await load();
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  }
}

async function remindIssueOwner(item: ModuleRecord): Promise<void> {
  try {
    const result = await eqsApi.remindModuleRecord('issues', 'issues', item.id);
    if (result.skipped) notify('error', '没催成', result.skipped);
    else notify('success', '已催办', `站内提醒已发给 ${result.ownerName ?? '责任人'}；一天只催一次。`);
  } catch (caught) {
    notify('error', '催办失败', errorMessage(caught));
  }
}

/*
  单元格就地改：和项目页共用 useIssueInlineUpdate 那一份写入路径。

  这里原先漏了 inline-update 这个事件——组件照样把六列渲染成可编辑，
  改完 emit 出去没人接，界面上像存上了，实际一次请求都没发（0823 发现）。
  少接一个事件不会报错，只能靠「改完刷新看看还在不在」发现，比接口报错难查得多。
*/
const { inlineUpdateIssue } = useIssueInlineUpdate(records, load);

/** 升级 / 降级写完库后只换这一条，不整表重载 */
function replaceIssue(saved: ModuleRecord): void {
  const index = records.value.findIndex((row) => String(row.id) === String(saved.id));
  if (index >= 0) records.value[index] = saved;
  else void load();
}

/**
 * 责任人候选：全公司通讯录。
 *
 * 项目页给的是本项目团队，全局清单跨项目，「本项目」在这里没有意义——
 * 取交集会把大多数人挡在外面，逐个项目去拉团队又是几十个请求。
 * 这个下拉本来就只是 datalist（给提示、不拦手打），公司通讯录正好合适。
 * 取不到就退回手打，不因为候选列表挂了就让人改不了责任人。
 */
const issueOwnerOptions = ref<string[]>([]);

async function loadIssueOwnerOptions(): Promise<void> {
  if (definition.value?.key !== 'issues' || issueOwnerOptions.value.length) return;
  try {
    const payload = await eqsApi.people() as { people?: Array<{ displayName?: string }> };
    issueOwnerOptions.value = [...new Set(
      (payload?.people ?? []).map((person) => String(person.displayName ?? '').trim()).filter(Boolean),
    )].sort((left, right) => left.localeCompare(right, 'zh-CN'));
  } catch {
    issueOwnerOptions.value = [];
  }
}

/*
  从项目页「转 8D」跳过来时带着 ?view=8d&issueId=…，
  打开就直接落在 8D 视图上，不用人再去点一次切换。
*/
const route = useRoute();

onMounted(() => {
  if (route.query.view === '8d') {
    issueView.value = '8d';
    const id = Number(route.query.issueId);
    if (Number.isFinite(id)) eightDFocusId.value = id;
  }
});

/**
 * 从 8D 报告点进来。带 step 时（点了进度条上某个空缺的 D 步）
 * 直接把 8D 新建表单展开并选好那一步——原来要打开问题再滚到抽屉最底部。
 */
const presetEightDStep = ref<string | null>(null);

function openFromBoard(issueId: number, step?: string): void {
  const record = records.value.find((item) => Number(item.id) === issueId);
  if (!record) {
    notify('error', '记录不在当前列表', '该问题可能不在你的可见项目范围内。');
    return;
  }
  // 先清空再设值：同一步连点两次也要能重新展开表单
  presetEightDStep.value = null;
  void selectRecord(record).then(() => {
    if (!step) return;
    presetEightDStep.value = step;
    void nextTick(() => drawer.value?.scrollToChildren());
  });
}

const maintenanceDialogOpen = ref(false);
let recordLoadToken = 0;

/*
  0916 Tony:「模块中心的项目管理里面的项目状态标识不清楚, 只有灰色英文字母体现,
  把整体 ui 再重新更新一下. 现在整个布局和整体已经不匹配了」。

  这张表和项目组合页看的是同一批项目，却是两套长相：
  那边的卡片已经分「开发中 / 草稿·未启动」并把草稿压暗，这边 20 行里
  11 个 deleted、1 个 archived、5 个 draft **和 14 个在跑的项目排在一起、
  状态全是灰的**。口径和措辞收到 utils/portfolio 那一份，两个页面说同一种话。
*/
type ProjectFilter = 'all' | 'started' | 'not-started' | 'ended';
const projectFilter = ref<ProjectFilter>('all');
const PROJECT_FILTERS: ReadonlyArray<{ key: ProjectFilter; label: string; hint: string }> = [
  { key: 'all', label: '全部', hint: '含已归档和已删除' },
  { key: 'started', label: '开发中', hint: '已经启动、在跑的项目' },
  { key: 'not-started', label: '草稿 / 未启动', hint: '还没正式开工的' },
  { key: 'ended', label: '已结束', hint: '已归档和已删除的' },
];

/** 这一行属于哪一档；非项目模块不用它 */
function projectBucketOf(record: ModuleRecord): ProjectFilter {
  const stage = projectStage(record as { status?: string | null });
  if (stage === 'archived' || stage === 'deleted') return 'ended';
  return stage;
}

const projectCounts = computed<Record<ProjectFilter, number>>(() => {
  const counts: Record<ProjectFilter, number> = { all: 0, started: 0, 'not-started': 0, ended: 0 };
  if (definition.value?.key !== 'projects') return counts;
  for (const record of searchedRecords.value) {
    counts.all += 1;
    counts[projectBucketOf(record)] += 1;
  }
  return counts;
});

/** 搜索 + 看板下钻筛完的；分档读数和列表都从它出发，保证数字和行数是同一件事 */
const searchedRecords = computed(() => {
  const q = query.value.trim().toLowerCase();
  // 下钻口径和画图共用 utils/module-dashboard，避免图上一个数、列表另一个数
  return records.value.filter((record) =>
    matchesDashboardDrill(props.moduleKey, record, dashboardFilter.value, dashboardLabels.value)
    && (!q || JSON.stringify(record).toLowerCase().includes(q)));
});

/*
  项目列表再按「开没开工」分档并重排：开发中在前、草稿其次、已结束沉底。
  和项目组合页同一个顺序——同一批数据在两个页面里的排法不该不一样。
*/
const PROJECT_ORDER: Readonly<Record<string, number>> = {
  started: 0, 'not-started': 1, archived: 2, deleted: 3,
};
const filteredRecords = computed(() => {
  if (definition.value?.key !== 'projects') return searchedRecords.value;
  return searchedRecords.value
    .filter((record) => projectFilter.value === 'all' || projectBucketOf(record) === projectFilter.value)
    .slice()
    .sort((left, right) =>
      (PROJECT_ORDER[projectStage(left as { status?: string | null })] ?? 9)
      - (PROJECT_ORDER[projectStage(right as { status?: string | null })] ?? 9));
});
const exportColumns = computed(() => definition.value
  ? recordExportColumns(definition.value.columns, definition.value.fields)
  : []);
/*
  问题清单自己管筛选和排序（它的看板下钻、六个筛选框、排序下拉都在组件里），
  所以导出要用它报上来的可见行；别的模块照旧用这一页的 dashboardFilter。
  0916 之前两边各算各的：在问题表里筛到 18 条，「导出当前结果」还是 50 条。
*/
const issueVisibleRows = ref<ModuleRecord[]>([]);
const exportSource = computed(() => (
  definition.value?.key === 'issues' ? issueVisibleRows.value : filteredRecords.value));
const exportRows = computed(() => recordExportRows(exportSource.value, exportColumns.value));
const exportFilename = computed(() =>
  buildExportFilename(['PDM', definition.value?.name || props.moduleKey]));
const issuesZipBusy = ref(false);
async function exportIssuesZip(): Promise<void> {
  const rows = exportSource.value;
  if (!rows.length || issuesZipBusy.value) return;
  const ids = rows.map((row) => Number(row.id)).filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) {
    notify('error', '导不出', '当前结果没有可导出的问题编号');
    return;
  }
  issuesZipBusy.value = true;
  try {
    const response = await fetch(eqsApi.issuesExportWithAttachmentsUrl(ids), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/zip, application/json' },
    });
    if (!response.ok) {
      let message = `导出失败（HTTP ${response.status}）`;
      try {
        const body = await response.json() as { message?: unknown };
        if (body?.message) message = Array.isArray(body.message) ? body.message.join('；') : String(body.message);
      } catch { /* keep default */ }
      notify('error', '导出含图失败', message);
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `PDM_问题_含图_${new Date().toISOString().slice(0, 10).replaceAll('-', '')}.zip`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    notify('error', '导出含图失败', error instanceof Error ? error.message : String(error));
  } finally {
    issuesZipBusy.value = false;
  }
}

const writable = computed(() => {
  if (!definition.value) return false;
  return definition.value.key === 'projects'
    || definition.value.mode === undefined
    || definition.value.mode === 'resource';
});
/**
 * 问题清单的格子能不能点。
 *
 * writable 只判「这个模块有没有写接口」，跟登录的人是谁无关；账号的写权限得另外看。
 * 只按 writable 给的话，只读账号（readonly / director 角色）在全局清单上看到的
 * 是一片能点的格子，点进去打完字回车，吃一条 403 红条——项目页那边一直是点不动的。
 * 两个页面同一个组件，判据也该是同一个。
 */
const issuesInlineEditable = computed(() => writable.value && session.canWrite.value);

const projectScoped = computed(() =>
  Boolean(definition.value?.fields.some((field) => field.key === 'projectId')));

/** 项目清单，供「关联项目」下拉使用 */
const projectOptions = ref<Array<{ value: string; label: string }>>([]);

/**
 * 其余「填内部 ID / 自由文本」的字段，同样换成下拉。
 *
 * 全局审核（2026-08-14）发现和「关联项目」一个毛病的还有：
 * 人力资源的「组织 ID」（库里 183 个部门）、PFMEA 的「关联 DFMEA 内部 ID」、
 * 以及变更单三个**必填**却没有任何选项的字段（变更类型/来源、发生阶段）——
 * 变更类型的 29 个官方取值一直躺在 cfg_dictionaries 里没送到表单上。
 *
 * key → [选项来源, 显示名, 提示]
 */

/**
 * 表单字段：把「关联项目内部 ID」换成项目下拉。
 *
 * 原来这里是个自由数字输入框，标签写着「关联项目内部 ID」——
 * 没有人知道 X04-C 的内部 ID 是几号，只能去别处翻，翻错了就挂错项目。
 * 生产库里 19 条工程变更有 15 条没关联项目，这个输入方式要负一部分责任。
 */
const formFields = computed<ModuleField[]>(() => {
  /*
    变更（0826 Tony）：通用字段区**整个不渲染**——那五组（变更标识/来源与关联/
    变更内容/台账与费用/实施与责任）和五步重复，删。填写口只剩五步：
    标题、内容、来源、类型、零件号、牵头人、关联项目在第 1 步表单；
    等级、风险、影响在第 2 步；客户批准在第 3 步；断点与标识在第 4 步。
    modules.ts 的字段定义**没删**：导出台账（exportColumns）仍按那份出列。
  */
  if (definition.value?.key === 'changes') return [];
  const fields = definition.value?.fields ?? [];
  const mapped = fields.map((field) => {
    if (field.key === 'projectId' && projectOptions.value.length) {
      return {
        ...field,
        label: '关联项目',
        type: 'select' as const,
        options: projectOptions.value.map((item) => item.value),
        optionLabels: Object.fromEntries(projectOptions.value.map((item) => [item.value, item.label])),
        help: '按项目编号选择；留空表示这条记录不挂到具体项目。',
      };
    }
    return field;
  });
  // 问题分类/发生阶段等标准选项库，和项目页共用同一份接线
  return withStandardOptions(mapped);
});

/**
 * 取一列的值：先看表列，再看 normalized。
 *
 * 问题/变更这类模块的业务字段（问题类型、等级、当前计划日期）存在 normalized 里，
 * 直接 record[key] 取会全是空——列加了却没数据，比不加更误导（2026-08-18）。
 */
function cellOf(record: ModuleRecord, key: string): unknown {
  const direct = record[key];
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  const bag = record.normalized;
  if (bag && typeof bag === 'object') return (bag as Record<string, unknown>)[key];
  return direct;
}

/** 列表里的关联项目列：内部 ID 翻译成「项目号 · 名称」，没挂上的写「待归属」 */
function projectCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '待归属';
  const hit = projectOptions.value.find((item) => item.value === String(value));
  return hit ? hit.label : String(value);
}

async function loadProjectOptions(): Promise<void> {
  if (!projectScoped.value || projectOptions.value.length) return;
  try {
    const payload = await eqsApi.portfolio();
    projectOptions.value = (payload?.projects ?? [])
      .map((project) => ({
        value: String(project.id),
        label: `${project.projectNo ?? project.id} · ${project.projectName ?? ''}`.trim(),
      }))
      .sort((left, right) => left.label.localeCompare(right.label, 'zh-CN'));
  } catch {
    // 取不到就退回原来的输入框，不因为下拉挂了就让人没法录入
    projectOptions.value = [];
  }
}
/** 审计里记的 entity_type；对不上的模块不显示入口 */
const historyEntityType = computed<string | null>(() => ({
  /*
    BOM 导入写审计时 entity_type 是 'bom'，但 **entity_id 传的是项目 id**
    （bom.service.ts）——和项目自己的审计共用同一个 id 空间。
    只查 'project' 的话，那批 BOM 导入记录就被劈掉了：线上项目 23 有
    4 条 project + 12 条 bom，抽屉里只看得到 4 条。
    这和 user/iam_user 是同一种「历史被劈成两半」，修法也一样：两个都查。
  */
  projects: 'project,bom',
  issues: 'issue',
  changes: 'change',
  dfmea: 'dfmea-document',
  'pfmea-cp': 'pfmea-document',
  // 同一张 iam_users 表被两处写入，类型名不一样，两个都要查
  hr: 'user,iam_user',
  /*
    files 这一行删了：/modules/files 的路由组件是 ModuleFilesView，
    不走这个视图（router/index.ts:87），这个分支永远不执行。
    留着会让人以为文件模块有历史入口——ProjectFilesExplorer 里也早有注释指出过这点。
  */
}[definition.value?.key ?? ''] ?? null));
/** 看板默认收起（1027 审查 P2-10）：执行页先给清单，要看图点「查看看板」 */
const dashboardOpen = ref(false);
const dashboardFilter = ref<{ kind: string; value: string } | null>(null);

/**
 * 变更类型 ID → 中文名（1027 复评 r2）。
 * 取的是变更表单下拉那一份 form-options：同一个类型 ID 在表单里叫什么，看板上就叫什么。
 *
 * 下拉那份按名字去过重，同名重复导入的其余 ID 不在里面——旧变更存的若是它们，
 * 就成了「未映射类型（原值）」。所以先垫别名表 changeTypeAliases（每个 ID 都有），
 * 再用 changeType 盖一遍：同一个 ID 两边名字本来就一样。老接口没有别名表，退回原样。
 */
const changeTypeLabels = computed<Record<string, string>>(() =>
  Object.fromEntries([
    ...(fieldOptions.value.changeTypeAliases ?? []),
    ...(fieldOptions.value.changeType ?? []),
  ].map((item) => [String(item.value), item.label])));

/**
 * 看板借的字典。画图（RecordListDashboard）和下钻（searchedRecords）传的是同一份——
 * 图上按「项目号 · 名称」分、列表却按记录自带字段筛，点柱子就会筛出空表。
 * loadProjectOptions / loadFormOptions 在 load() 开头就和列表并行取，看板打开前已经在路上了。
 */
const dashboardLabels = computed<DashboardLabels>(() => ({
  projects: projectLabels.value,
  changeTypes: changeTypeLabels.value,
}));

function drill(filter: { kind: string; value: string }): void {
  dashboardFilter.value = filter;
  query.value = '';
}

// 换模块时把上一个模块的下钻条件丢掉，否则会筛出一个空列表
watch(() => props.moduleKey, () => { dashboardFilter.value = null; });
/*
  项目清单 / 类型字典比列表晚到时，之前按旧名字（「未解析项目（#7）」）点下的柱子
  会和新名字对不上、把列表筛空——同一个道理，丢掉。
*/
watch(dashboardLabels, () => {
  if (dashboardFilter.value?.kind === 'project' || dashboardFilter.value?.kind === 'changetype') {
    dashboardFilter.value = null;
  }
});

const maintenanceAvailable = computed(() => Boolean(definition.value && [
  'dfmea',
  'pfmea-cp',
  'files',
  'issues',
  'changes',
].includes(definition.value.key)));
const selectedProjectPath = computed(() =>
  selected.value ? recordProjectPath(selected.value) || null : null);
const availableStatusActions = computed(() => {
  if (!selected.value || !definition.value) return [];
  if (definition.value.key === 'projects') {
    return projectStatusActions(String(selected.value.status || ''));
  }
  const transitions: Record<string, string[]> = {
    draft: ['submitted', 'active', 'cancelled'],
    submitted: ['approved', 'rejected', 'cancelled'],
    approved: ['active', 'obsolete'],
    open: ['in_progress', 'closed', 'cancelled'],
    pending: ['in_progress', 'completed', 'rejected', 'cancelled'],
    in_progress: ['completed', 'closed', 'blocked', 'cancelled'],
    blocked: ['in_progress', 'cancelled'],
    uploaded: ['released', 'rejected'],
    released: ['obsolete'],
    active: ['inactive', 'obsolete', 'closed'],
    rejected: ['draft', 'in_progress'],
  };
  const allowed = transitions[String(selected.value.status || '')] || [];
  return definition.value.statusActions.filter((action) => {
    if (!allowed.includes(action.value)) return false;
    /*
      变更的「取消（作废）」搬到第 5 步卡片里了，和「中途关闭」并排
      （0828 Tony:「这个状态操作是不是重复了啊」）。这里摘掉它，
      免得同一个动作在抽屉里出现两次。
      **只摘这一个**：「退回修改」这类还留在状态操作里，它没有别的去处——
      整块藏掉会把它一起弄丢。
    */
    return !(definition.value?.key === 'changes' && action.value === 'cancelled');
  });
});

function resetForm() {
  recordLoadToken += 1;
  recordLoading.value = false;
  recordDialogOpen.value = false;
  editorStage.value?.close();
  selected.value = null;
  form.value = Object.fromEntries((definition.value?.fields || []).map((field) => [field.key, '']));
  importPreview.value = null;
}

/**
 * 只有管理员和项目经理能开新项目（后端 ProjectWriteController 是那道真拦截）。
 * 其余模块的「新建」不受影响——收的只是项目这一个口子。
 */
const canCreateProjects = session.canCreateProjects;

function startCreate() {
  resetForm();
  creatingProject.value = definition.value?.key === 'projects';
  recordDialogOpen.value = !creatingProject.value;
}

function cancelProjectCreation() {
  creatingProject.value = false;
}

async function handleProjectCreated(project: Project) {
  creatingProject.value = false;
  await load();
  await router.push(`/projects/${project.id}/overview`);
}

function populateForm(record: ModuleRecord) {
  editorStage.value?.close();
  selected.value = record;
  const keyMap: Record<string, string> = { projectId: 'project_id', parentId: 'parent_id', ownerName: 'owner_name', dueAt: 'due_at' };
  const recordNormalized = record.normalized && typeof record.normalized === 'object'
    ? record.normalized as Record<string, unknown>
    : {};
  form.value = Object.fromEntries((definition.value?.fields || []).map((field) => {
    const direct = record[keyMap[field.key] || field.key];
    const value = direct === undefined || direct === null ? recordNormalized[field.key] : direct;
    const displayValue = field.type === 'date' && typeof value === 'string' ? value.slice(0, 10) : value;
    return [field.key, formatCell(displayValue) === '—' ? '' : String(displayValue ?? '')];
  }));
  // 附件、版本、在线编辑状态由 RecordDrawer 自己按 record.id 拉，这里不再各拉一遍
}

function recordProjectPath(record: ModuleRecord): string {
  if (!definition.value) return '';
  if (definition.value.key === 'projects') {
    const projectId = String(record.id ?? '').trim();
    return /^[1-9]\d*$/.test(projectId) ? `/projects/${projectId}/overview` : '';
  }
  return projectPathForModule(definition.value.module, record.project_id) || '';
}

async function openProject(record: ModuleRecord) {
  const path = recordProjectPath(record);
  if (!path) {
    notify('error', '无法打开项目', '当前记录缺少可用的项目内部 ID。');
    return;
  }
  await router.push(path);
}

async function selectRecord(record: ModuleRecord) {
  populateForm(record);
  recordDialogOpen.value = true;
  if (definition.value?.key !== 'projects') return;
  const token = ++recordLoadToken;
  recordLoading.value = true;
  try {
    const detail = await eqsApi.projectDetail(record.id);
    if (token === recordLoadToken && selected.value?.id === record.id) {
      populateForm(detail as unknown as ModuleRecord);
    }
  } catch (error) {
    notify('error', '项目详情读取失败', errorMessage(error));
  } finally {
    if (token === recordLoadToken) recordLoading.value = false;
  }
}

/**
 * 变更台账的扩展列 + 费用列可见性（0826）。
 * 取不到就当不可见、扩展列显示 —：台账主体（行）不靠它，别让一个辅助接口拖垮列表。
 */
const ledgerExtras = ref<Record<string, {
  attachmentCount: number; appliedAt: string | null; closedAt: string | null; costAllowed: boolean;
  /** 这一步做完了、等人推进（0828）；台账上标「待推进」 */
  stepReady?: boolean;
}>>({});
const changeCostVisible = ref(false);

/*
  这条变更挂了几份资料（0828 关闭确认要看）。
  复用台账扩展列已经查过的数——再单独查一次是白费；
  查不到就当 0，宁可提醒人去传，不装作有。
*/
const closureAttachmentCount = computed(() => {
  const id = selected.value?.id;
  return id === undefined ? 0 : (ledgerExtras.value[String(id)]?.attachmentCount ?? 0);
});
async function loadLedgerExtras(): Promise<void> {
  if (definition.value?.key !== 'changes') return;
  try {
    const payload = await eqsApi.changeLedgerExtras();
    changeCostVisible.value = Boolean(payload.costVisible);
    ledgerExtras.value = Object.fromEntries(payload.items.map((item) => [String(item.id), item]));
  } catch {
    changeCostVisible.value = false;
    ledgerExtras.value = {};
  }
}
/**
 * 打开的这条变更能不能看/填费用（第 5 步「台账与费用」块）。
 * 跨项目清单里 PM 只对自己项目的行有权限——列显示了不等于每条都能填。
 */
const selectedCostVisible = computed(() => {
  if (!changeCostVisible.value) return false;
  if (!selected.value) return changeCostVisible.value;
  const extra = ledgerExtras.value[String(selected.value.id)];
  return extra ? extra.costAllowed : changeCostVisible.value;
});

/**
 * 全局问题清单每一行「我能改什么 / 能不能关」（0928）。
 *
 * 后端只有按单个项目查的路由；清单跨项目，就按出现的项目各取一次、合成一张表。
 * 某个项目取失败不连累别的项目：那几行按兜底处理，后端照样会拦。
 */
const issueRights = ref<Record<string, IssueEditRights>>({});
async function loadIssueRights(items: readonly ModuleRecord[]): Promise<void> {
  const projectIds = [...new Set(items.map((item) => String(item.project_id ?? '')).filter(Boolean))];
  const one = async (projectId: string): Promise<Record<string, IssueEditRights>> => {
    try {
      return await eqsApi.issueEditRights(projectId);
    } catch {
      return {};
    }
  };
  issueRights.value = Object.assign({}, ...await Promise.all(projectIds.map(one)));
}

async function load() {
  if (!definition.value) return;
  void loadProjectOptions();   // 与列表并行取，取不到也不挡住主流程
  void loadFormOptions();     // 部门 / 变更类型 / 问题分类等下拉，同上
  void loadIssueOwnerOptions();  // 问题清单的责任人候选，同上
  void loadLedgerExtras();    // 变更台账的附件数/日期/费用可见性，同上
  loading.value = true;
  listError.value = '';
  summaryError.value = '';
  try {
    const payload = await eqsApi.moduleRecords(
      definition.value.module,
      definition.value.resource,
      definition.value.mode || 'resource',
      // 已删除和已归档的项目只给管理员和总监看（0902 Tony）；后端还会再拦一道
      { includeArchived: session.canViewDirectorBoard.value },
    );
    if (!payload || !Array.isArray(payload.items)) throw new Error('模块列表响应缺少 items 数组');
    records.value = payload.items;
    if (definition.value.key === 'issues') void loadIssueRights(payload.items);
  } catch (error) {
    records.value = [];
    listError.value = errorMessage(error);
  }
  try {
    const catalog = await eqsApi.moduleCatalog();
    catalogItem.value = catalog.find((item) => item.key === definition.value?.module) || null;
  } catch (error) {
    catalogItem.value = null;
    summaryError.value = errorMessage(error);
  } finally {
    loading.value = false;
  }
}


/**
 * 子条目动作（8D 步骤 / 变更措施的「完成」）之前，先把抽屉表单存了。
 *
 * 试用反馈 0819：「填完日期等信息仍不能提交断点完成」。
 * 「断点实施」要求父变更的「实际断点」非空——人在表单里填了、没点保存，
 * 就去点措施的「完成」，后端读库当然是空的，于是报「实际断点还是空的」，
 * 而那个值明明就显示在屏幕上。不是后端判错，是中间少了一步保存。
 *
 * 只在真的有未保存改动时才打断；干净的表单直接放行，不给人加一次点击。
 */
async function saveBeforeChildAction(): Promise<boolean> {
  const dialog = recordDialog.value;
  if (!dialog?.isDirty()) return true;
  const confirmed = await confirmAction({
    title: '表单里有还没保存的修改',
    message: '子条目的状态直接写进库，但你刚填的内容还只在页面上。先保存再继续，否则后端看不到。',
    confirmLabel: '保存后继续',
  });
  if (!confirmed) return false;
  await save(dialog.getValues());
  return true;
}

async function save(values: Record<string, string>) {
  if (!definition.value) return;
  form.value = values;
  if (!writable.value) {
    notify('error', '后端能力未开放', `${definition.value.name}当前仅提供读取接口，创建/编辑接口尚未开放。`);
    return;
  }
  /*
    这里原来还有一份必填校验，校验不过就弹个 toast 然后 return。
    问题是 RecordFormDialog 里已经有一份、而且会在表单里就地标红——
    父层这一份只弹一下就没了，人只看到「点了保存没反应」，
    以为刚勾的影响范围没保存（Tony 2026-08-18 的「无法保持」就是这个）。
    校验只留对话框那一处：能看见改哪里，不会错过。
  */
  saving.value = true;
  try {
    if (definition.value.key === 'projects') {
      const payload = buildProjectWritePayload(form.value);
      const saved = selected.value
        ? await eqsApi.updateProject(selected.value.id, payload)
        : await eqsApi.createProject(payload);
      notify(
        'success',
        selected.value ? '保存成功' : '创建成功',
        saved.projectNoConflict
          ? `项目已保存，但项目编号与内部 ID ${saved.conflictingProjectIds?.join('、') || '其他项目'} 重复。`
          : '项目已写入项目主数据接口。',
      );
      await load();
      recordDialogOpen.value = false;
      selected.value = null;
      return;
    }
    const payload: Record<string, unknown> = {};
    const normalized: Record<string, unknown> = {};
    const coreKeys = new Set(['projectId', 'parentId', 'code', 'title', 'priority', 'ownerName', 'dueAt', 'revision']);
    for (const field of definition.value.fields) {
      const raw = form.value[field.key]?.trim() ?? '';
      if (!raw) continue;
      const value: unknown = field.type === 'number' ? Number(raw) : raw;
      if (coreKeys.has(field.key)) payload[field.key] = value;
      else normalized[field.key] = value;
    }
    if (Object.keys(normalized).length) payload.normalized = normalized;
    for (const key of ['projectId', 'parentId']) {
      if (payload[key] !== undefined && payload[key] !== '') payload[key] = Number(payload[key]);
      else delete payload[key];
    }
    if (typeof payload.dueAt === 'string' && payload.dueAt) {
      payload.dueAt = new Date(`${payload.dueAt}T23:59:59+08:00`).toISOString();
    } else {
      delete payload.dueAt;
    }
    const saved = selected.value
      ? await eqsApi.updateModuleRecord(definition.value.module, definition.value.resource, selected.value.id, payload)
      : await eqsApi.createModuleRecord(definition.value.module, definition.value.resource, payload);
    notify('success', selected.value ? '保存成功' : '创建成功', `${definition.value.entityName}已写入业务接口。`);
    await load();
    recordDialogOpen.value = false;
    selected.value = null;
  } catch (error) {
    notify('error', '保存失败', errorMessage(error));
  } finally {
    saving.value = false;
  }
}

function downloadTemplate() {
  if (!definition.value) return;
  window.location.assign(eqsApi.moduleTemplateUrl(definition.value.module, definition.value.resource));
}

async function previewImport() {
  const file = importInput.value?.files?.[0];
  if (!definition.value || !file) {
    notify('error', '请选择模板文件', '请先选择填写完成的 CSV 模板。');
    return;
  }
  const payload = new FormData();
  payload.append('file', file);
  importLoading.value = true;
  importPreview.value = null;
  try {
    importPreview.value = await eqsApi.previewModuleImport(
      definition.value.module,
      definition.value.resource,
      payload,
    );
    notify(
      importPreview.value.invalid ? 'error' : 'success',
      '导入预览完成',
      `共 ${importPreview.value.total} 行，有效 ${importPreview.value.valid} 行，异常 ${importPreview.value.invalid} 行；尚未写入数据。`,
    );
  } catch (error) {
    notify('error', '导入预览失败', errorMessage(error));
  } finally {
    importLoading.value = false;
  }
}

async function confirmImport() {
  if (!definition.value || !importPreview.value || importPreview.value.invalid) return;
  if (!await confirmAction({ title: `确认写入 ${importPreview.value.total} 行？其中新增 ${importPreview.value.creates} 行、按内部 ID 更新 ${importPreview.value.updates} 行。` })) return;
  const rows = importPreview.value.rows.map((row) => ({
    ...(row.recordId ? {recordId: row.recordId} : {}),
    ...row.values,
  }));
  importConfirming.value = true;
  try {
    const result = await eqsApi.confirmModuleImport(
      definition.value.module,
      definition.value.resource,
      rows,
    );
    notify('success', '模板导入完成', `新增 ${result.created} 行，更新 ${result.updated} 行。`);
    importPreview.value = null;
    if (importInput.value) importInput.value.value = '';
    maintenanceDialogOpen.value = false;
    await load();
  } catch (error) {
    notify('error', '模板导入失败', errorMessage(error));
  } finally {
    importConfirming.value = false;
  }
}

/**
 * 抽屉抬头。
 *
 * 变更单独带上编号和标题：这两格摘进第 1 步表单之后，抬头是唯一还能看出
 * 「我在看哪条」的地方，而变更编号是程序文件规定的唯一追溯码。
 * 别的模块编号标题还在表单第一格摆着，不跟着改（改了就动到别人的界面）。
 */
const recordDialogTitle = computed(() => {
  const entity = definition.value?.entityName ?? '记录';
  if (!selected.value) return `新建${entity}`;
  const base = `编辑${entity}`;
  return definition.value?.key === 'changes' ? changeDialogTitle(base, selected.value) : base;
});

/**
 * 五步里的写入类按钮（生成清单、催办、铺任务、勾选）给不给。
 *
 * writable 只判「这个模块有没有写接口」，跟登录的人是谁无关；
 * 只读账号看到的会是一排点下去必然吃 403 的按钮——和问题清单的格子同一个口径。
 */
const changeStepWritable = computed(() => writable.value && session.canWrite.value);

/** 记录被外部动过的次数：状态按钮改完 +1，用来叫醒五步条（它只 watch changeId） */
const recordRefreshTick = ref(0);

/**
 * 新建工程变更（0826）：新建屏 = 第 1 步表单，值和凭证文件从五步条里 emit 上来。
 * 创建 →（选了文件就）传附件 → 提交第 1 步 的链路在 composable 里共享；
 * 建完**不关抽屉**：切到编辑态，附件区和「提交 / 下一步」就在原地，人接着走。
 */
const changeCreating = ref(false);
async function createChangeFromStep(payload: {
  values: Record<string, string>; file: File | null; customerImages?: File[];
}): Promise<void> {
  const module = definition.value;
  if (!module || changeCreating.value) return;
  if (!writable.value) {
    notify('error', '后端能力未开放', `${module.name}当前仅提供读取接口。`);
    return;
  }
  changeCreating.value = true;
  try {
    const record = await createChangeViaStepForm(
      (body) => eqsApi.createModuleRecord(module.module, module.resource, body),
      payload,
    );
    if (!record) return;
    await load();
    const fresh = records.value.find((item) => String(item.id) === String(record.id)) ?? record;
    await selectRecord(fresh as ModuleRecord);
  } finally {
    changeCreating.value = false;
  }
}

/** 五步里动了东西（推进 / 生成清单 / 勾措施）：列表要跟着变，推进现在也会改状态 */
async function onChangeStepChanged(): Promise<void> {
  await load();
  const current = selected.value;
  if (!current) return;
  /*
    选中的这条也换成库里最新的——但**只在状态真的变了时换**：
    换 selected 会让表单按新初值重建，人填了一半没保存的东西就没了。
  */
  const fresh = records.value.find((item) => String(item.id) === String(current.id));
  if (fresh && String(fresh.status ?? '') !== String(current.status ?? '')) populateForm(fresh);
}

/*
  作废这条变更（0828）：入口从抽屉最下面的「状态操作」搬到了第 5 步的卡片里，
  和「中途关闭」并排。动作本身还是同一条状态流转，只是问一次确认。
*/
async function voidChange(): Promise<void> {
  if (!selected.value) return;
  const ok = await confirmAction({
    title: '作废这条变更',
    message: '整条撤掉，不再流转，台账上标「终止」。已执行过的动作要按程序 10.4 发反向变更恢复原状。',
  });
  if (!ok) return;
  await changeStatus({ value: 'cancelled', label: '取消' } as DrawerStatusAction);
}

/* 确认弹窗由 RecordDrawer 统一问过了，这里只管执行 */
async function changeStatus(action: DrawerStatusAction) {
  if (!selected.value || !definition.value) return;
  if (!writable.value) {
    notify('error', '后端能力未开放', '当前记录没有可用的状态流转接口。');
    return;
  }
  try {
    /*
      问题的状态只走问题专用的那条路（0928）：它认得出提出人 / 项目经理 / 责任人，
      谁能关在那里判；通用的状态接口对问题不再接。
    */
    const updated = definition.value.key === 'projects'
      ? await eqsApi.advanceProjectStatus(selected.value.id, action.value as ProjectStatus)
      : definition.value.key === 'issues'
        ? await eqsApi.advanceIssueRecord(selected.value.id, action.value)
        : await eqsApi.updateModuleStatus(
          definition.value.module,
          definition.value.resource,
          selected.value.id,
          action.value,
        );
    notify('success', '状态已更新', `当前状态：${updated.status || action.value}`);
    await load();
    selectRecord(updated as unknown as ModuleRecord);
    /*
      五步条只 watch changeId：在同一条记录上点完「退回」，它还显示在原来那一步。
      换个 refreshKey 让它自己去后端问一遍（0824）。
    */
    recordRefreshTick.value += 1;
  } catch (error) {
    notify('error', '状态更新失败', errorMessage(error));
  }
}

/**
 * 抽屉的文件区形态。
 * 文件模块走 DMS 正式版本（documents/:id/versions），
 * 其余业务模块挂 module-tools 附件；项目主数据本身不挂文件。
 */
/*
  业务附件放什么，得写出来（0828 Tony:「下面的附件也没有明确的说明」）。
  原来只有一句通用的「实际内容以文件为准…」，人不知道该往里传什么，
  于是要么不传，要么把该进第 1 步「变更内容附件」的东西传到这里。
*/
/* 变更那一句 0920 起用不上了：变更抽屉不再摆这一块（见下面 drawerFileMode） */
const drawerFileHint = '实际内容以文件为准。再次上传会保留旧文件，形成可下载的更新历史；单文件最大 1GB。';

  /*
    0920 Tony:「业务附件啥的其实没啥用，可以去掉这一块」。

    变更的文件现在全挂在五步里各自那一格下面：第 1 步「变更内容附件」是 catch-all
    （没归属的也在那儿，能传能下能撤），客户要求原文、可行性方案、客户批准 / 告知记录
    各自独立成段。抽屉底下这一块就是同一批文件再列一遍。

    摘之前先查了它挡不挡门禁（0828 的规矩：藏起来又挡路是最坏的组合）——
    第 1 步「至少 1 个附件」读的是同一张表，而上传 / 下载 / 撤下在「变更内容附件」
    那一栏全都有：门还在，入口也还在，只是不再重复摆一份。
  */
const drawerFileMode = computed<'versions' | 'attachments' | 'none'>(() => {
  if (!definition.value) return 'none';
  if (definition.value.key === 'files') return 'versions';
  if (definition.value.key === 'changes') return 'none';
  return definition.value.key === 'projects' ? 'none' : 'attachments';
});

/** 抽屉里的子条目：问题挂 8D 步骤，变更挂执行清单 */
const drawerChildren = computed<DrawerChildList | null>((): DrawerChildList | null => {
  if (definition.value?.key === 'issues') {
    return {
      module: 'issues',
      resource: 'steps',
      title: '8D 步骤（D0–D8）',
      emptyHint: '这条问题还没有 8D 步骤。需要走八步法就点上面「一键建好 D0–D8」；单独补一步用右上「＋ 添加」。',
      extraFields: { remarks: '备注', category: '分类' },
      stepOptions: ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8'],
      presetStep: presetEightDStep.value,
    };
  }
  /*
    变更的执行清单不在这里挂了：它就是五步里第 4、5 步的任务本身，
    抽屉底下再挂一份等于同一批措施显示两遍，勾在哪一份里全看人当时滚到哪。
    连同 extraFields（变更内容/功能影响/外观影响）一起搬进 ChangeStepBar 的第 4 步。
    8D 那支不动。
  */
  return null;
});

/**
 * 变更状态区剩下的两个按钮，各自说清楚「点了会怎样」。
 *
 * 推进类的四个按钮撤了（推进只走五步条的「推进到第 N 步」，它会一并改状态），
 * 状态区不写一句就成了「这里怎么只剩两个按钮」——留下来的这两条恰恰是
 * 五步表达不了的：五步只前进，退回和作废没地方去。
 */
/*
  1028 Soft-Gate P2：退回和作废在台账上是两个词——退回标「已退回」（还要重填往下走），
  作废标「终止」（见 voidChange 的确认语）。两条说明各自点名，和台账、看板用同一个词。
*/
const CHANGE_STATUS_HINTS: Readonly<Record<string, string>> = {
  rejected: '评审没过，打回去重填，台账上标「已退回」。往前推进走上面五步的「推进到第 N 步」，推进会一并改状态。',
  cancelled: '这条变更作废，不再流转。已经执行过的动作要按程序 10.4 发起反向变更恢复原状。',
};

/**
 * 状态区一个按钮都没有时说清楚下一步在哪。
 *
 * 变更经常就是这样（已批准 / 实施中这两个状态下，推进类按钮撤走后没剩别的）——
 * 不说的话 RecordDrawer 会兜底成一句红字「当前状态没有可执行的下一步」，
 * 而下一步明明就在上面的五步里，那句话在这里是假话。
 */
const drawerStatusEmptyHint = computed(() => (definition.value?.key === 'changes' && selected.value
  ? '往前推进走上面五步的「推进到第 N 步」，推进会一并改状态；这个状态下没有别的可点。'
  : ''));

/** 状态区的抬头：项目主数据那条另有措辞 */
const drawerStatusActions = computed<DrawerStatusAction[]>(() =>
  availableStatusActions.value.map((action) => ({
    value: action.value,
    label: action.label,
    danger: action.danger,
    hint: definition.value?.key === 'changes' ? CHANGE_STATUS_HINTS[action.value] : undefined,
  })));

async function openEditorHere(): Promise<void> {
  if (!selected.value) return;
  await editorStage.value?.open(selected.value.id);
}

watch(() => props.moduleKey, () => {
  creatingProject.value = false;
  maintenanceDialogOpen.value = false;
  resetForm();
  void load();
}, { immediate: true });

</script>

<template>
  <PageState v-if="!definition" state="error" title="模块定义不存在" :message="`未知模块：${moduleKey}`" />
  <template v-else>
    <header class="module-context">
      <div class="module-code">{{ definition.code }}</div>
      <div><span>MODULE / {{ definition.key.toUpperCase() }}</span><p>{{ definition.caption }}</p></div>
      <!-- 「重算业务指标」0916 挪到用户管理页的运维区：它是运维动作，不是模块里的业务 -->
      <RouterLink v-if="definition.key === 'projects'" class="button button-secondary" to="/portfolio">进入项目组合</RouterLink>
    </header>

    <aside v-if="summaryError" class="api-warning"><b>统计接口不可用</b><span>{{ summaryError }}</span></aside>
    <section v-if="catalogItem" class="module-stat-strip">
      <div><span>记录总数</span><strong>{{ catalogItem.count }}</strong></div>
      <!--
        1028 Soft-Gate P2（复评步骤 02：顶部待处理 18、看板未结束 20）：后端「待处理」按
        draft / open / pending / in_progress / blocked / submitted 计（modules.service.ts），不含 rejected；
        看板「未结束」= 非结束态，含已退回。差的就是已退回那几条，悬停说清，不在条上再加一行字。
        1028 Soft-Gate copy-tail（复评 r2 步骤 10）：只靠悬停，复评仍读不出差在哪——变更的眉标直接写
        「待处理·不含退回」，和看板「未结束·含退回」成对；仍不加整行说明，其余模块照旧。
      -->
      <div :title="definition.key === 'changes' ? '待处理 = 进行中+已提交等（不含已退回）；未结束 = 待处理+已退回' : undefined"><span>{{ definition.key === 'changes' ? '待处理·不含退回' : '待处理' }}</span><strong>{{ catalogItem.openCount }}</strong></div>
      <div :class="{ risk: catalogItem.health === 'risk' }"><span>模块状态</span><strong>{{ catalogItem.health === 'risk' ? '异常' : catalogItem.health === 'warning' ? '关注' : '正常' }}</strong></div>
      <div><span>最近更新</span><strong class="date">{{ formatDate(catalogItem.refreshedAt) }}</strong></div>
    </section>

    <!-- 全屏编辑台要在抽屉外面：开编辑器时抽屉会关，挂里面就跟着卸载了 -->
    <OnlyOfficeStage ref="editorStage" @show="recordDialogOpen = false" />

    <div class="workbench-grid workbench-grid-full">
      <section class="content-panel record-panel">
        <header class="section-heading">
          <div><span>RECORD LIST</span><h2>{{ definition.entityName }}列表</h2><p>你可见范围内的全部{{ definition.entityName }}；点击行打开详情与操作。</p></div>
          <div class="section-heading-actions">
            <ExportDataButton
              :columns="exportColumns"
              :rows="exportRows"
              :filename="exportFilename"
              label="导出当前结果"
            />
            <button
              v-if="definition.key === 'issues'"
              class="button button-secondary"
              type="button"
              :disabled="!exportSource.length || issuesZipBusy"
              :title="exportSource.length ? `把当前结果打包成 ZIP（清单 + 图片），共 ${exportSource.length} 条` : '暂无可导出数据'"
              data-export-issues-zip
              @click="exportIssuesZip"
            >{{ issuesZipBusy ? '正在打包…' : '导出含图 ZIP' }}<span v-if="exportSource.length">（{{ exportSource.length }}）</span></button>
            <a v-if="definition.key === 'pfmea-cp'" class="button button-secondary" :href="eqsApi.pfmeaWorksheetTemplateUrl()">下载 PFMEA 表格模板</a>
            <button v-if="maintenanceAvailable" class="button button-secondary" type="button" @click="maintenanceDialogOpen = true">模板批量维护</button>
            <button
              v-if="definition.key !== 'projects' || canCreateProjects"
              class="button button-primary"
              type="button"
              @click="startCreate"
            >＋ 新建{{ definition.entityName }}</button>
          </div>
        </header>
        <p v-if="definition.key === 'projects' && !selected" class="record-hint">
          点击项目行或“进入项目工作区”直接打开项目组合中的对应项目；需要改基础资料时再点“编辑资料”。
        </p>
        <!--
          问题模块不挂这块通用看板（0916 Tony:「问题清单模块怎么有两个 dashboard」）。

          下面 ProjectIssueList 自带的 IssueDashboard 是同一组数字的**更全版本**
          （多了已逾期、升级层级、老化分桶），两块叠在一起是同一份数据摆两遍；
          而且更糟的是**这块的下钻筛不动下面的清单**——问题清单拿的是 records
          不是 filteredRecords，点柱子只会让顶上出现一句「已按 B-重要 筛选, 18 条」，
          下面还是 50 条，连「导出当前结果 (18)」都和屏幕上看到的对不上。
          它独有的「未设完成日期」已经挪进 IssueDashboard。
        -->
        <RecordListDashboard
          v-if="!loading && definition.key !== 'issues'"
          v-model="dashboardFilter"
          v-model:open="dashboardOpen"
          :module-key="moduleKey"
          :entity-name="definition.entityName"
          :items="records"
          :filtered-count="filteredRecords.length"
          :project-labels="projectLabels"
          :change-type-labels="changeTypeLabels"
        />
        <div v-if="definition.key === 'issues'" class="issue-view-tabs">
          <button type="button" class="inline-button" :class="{ on: issueView === 'list' }" @click="issueView = 'list'">问题清单</button>
          <button type="button" class="inline-button" :class="{ on: issueView === '8d' }" @click="issueView = '8d'">8D 报告</button>
        </div>
        <Issue8dBoard v-if="definition.key === 'issues' && issueView === '8d'" @open="openFromBoard" />
        <!--
          项目列表的分档（0916）。20 行里 11 个已删除、1 个已归档、5 个草稿，
          原来和 14 个在跑的项目排在一起、状态全是灰的。
          读数只数**搜索筛完之后**的行，和点进去看到的是同一批。
        -->
        <nav v-if="definition.key === 'projects'" class="project-filters" aria-label="按启动状态分档">
          <button
            v-for="entry in PROJECT_FILTERS"
            :key="entry.key"
            type="button"
            class="project-filter"
            :class="{ 'is-active': projectFilter === entry.key }"
            :aria-pressed="projectFilter === entry.key"
            :title="entry.hint"
            @click="projectFilter = entry.key"
          >{{ entry.label }} <b>{{ projectCounts[entry.key] }}</b></button>
        </nav>
        <label v-if="definition.key !== 'issues'" class="table-search"><span>筛选记录</span><input v-model="query" type="search" placeholder="输入任意字段" /></label>
        <template v-if="definition.key === 'issues' && issueView === 'list'">
          <PageState v-if="loading" state="loading" title="正在读取模块数据" message="连接业务接口。" />
          <PageState v-else-if="listError" state="error" title="模块列表不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="listError" @retry="load" />
          <!-- 和项目页同一个组件：行内编辑、附件、催办、8D、快速加行全都一致 -->
          <!--
            :edit-rights 按行灰掉「你改不了的格子」、决定状态下拉给不给「关闭」。
            后端那条路由按单个项目查，这里按清单里出现的项目各取一次再合起来（0928）——
            随手塞一个项目 ID 会按 A 项目的身份去判 B 项目的问题。没挂项目的问题拿不到，
            按「都能点 / 质量能关」兜底，分层仍由后端 assertIssueEditable 拦住并说清谁能改。
          -->
          <ProjectIssueList
            v-else
            :items="records"
            :can-edit="issuesInlineEditable"
            :can-close-issues="session.canCloseIssues.value"
            :edit-rights="issueRights"
            :owner-options="issueOwnerOptions"
            show-project
            :project-labels="projectLabels"
            @edit="selectRecord"
            @create="startCreate"
            @assist="startCreate"
            @remind="remindIssueOwner"
            @quick-create="quickCreateIssueGlobal"
            @inline-update="inlineUpdateIssue"
            @escalated="replaceIssue"
            @open8d="selectRecord"
            @view-change="issueVisibleRows = $event"
          />
        </template>
        <template v-if="definition.key !== 'issues'">
        <PageState v-if="loading" state="loading" title="正在读取模块数据" message="连接业务接口。" />
        <PageState v-else-if="listError" state="error" title="模块列表不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="listError" @retry="load" />
        <!--
          变更走台账样式（0826 Tony：照老 Excel 变更台账的列）。
          费用四列只有管理员和本项目项目经理看得到（changeCostVisible，后端同口径裁行）。
        -->
        <template v-else-if="definition.key === 'changes'">
          <ChangeLedgerTable
            v-if="filteredRecords.length"
            :items="filteredRecords"
            :extras="ledgerExtras"
            :attachment-count="closureAttachmentCount"
            :cost-visible="changeCostVisible"
            show-project
            :project-labels="projectLabels"
            :selected-id="selected?.id ?? null"
            @edit="selectRecord"
          />
          <PageState v-else state="empty" title="暂无记录" message="接口已返回空列表，可点击上方按钮创建第一条记录。" />
        </template>
        <div v-else class="data-table-wrap">
          <table class="data-table data-table--rows-link">
            <thead><tr><th v-for="column in definition.columns" :key="column.key">{{ column.label }}</th><th>状态</th><th>操作</th></tr></thead>
            <tbody>
              <tr
                v-for="record in filteredRecords"
                :key="record.id"
                :class="[
                  { 'is-selected': selected?.id === record.id, 'project-row': definition.key === 'projects' },
                  definition.key === 'projects' ? `stage-${projectStage(record as { status?: string | null })}` : '',
                ]"
                :tabindex="definition.key === 'projects' ? 0 : undefined"
                @click="definition.key === 'projects' ? openProject(record) : selectRecord(record)"
                @keydown.enter="definition.key === 'projects' ? openProject(record) : selectRecord(record)"
              >
                <td v-for="column in definition.columns" :key="column.key">
                  <RouterLink
                    v-if="definition.key === 'projects' && column.key === 'projectName'"
                    class="project-name-link"
                    :to="recordProjectPath(record)"
                    @click.stop
                  >{{ formatCell(record[column.key]) }} <span>打开 →</span></RouterLink>
                  <!-- 关联项目列显示项目号，不显示内部 ID；没挂上的明说「待归属」 -->
                  <template v-else-if="column.key === 'project_id'">{{ projectCell(record.project_id) }}</template>
                  <template v-else>{{ formatCell(cellOf(record, column.key)) }}</template>
                </td>
                <!--
                  项目的状态列用和项目组合页同一套措辞（开发中 / 草稿·未启动 /
                  报价中 / 已归档 / 已删除），别一个页面说 deleted、另一个说已删除。
                  别的模块照旧由 StatusChip 自己翻。
                -->
                <td>
                  <StatusChip
                    :value="record.status"
                    :label="definition.key === 'projects'
                      ? projectStageLabel(record as { status?: string | null })
                      : undefined"
                  />
                </td>
                <td>
                  <RouterLink
                    v-if="recordProjectPath(record)"
                    class="inline-button project-open-button"
                    :to="recordProjectPath(record)"
                    @click.stop
                  >{{ definition.key === 'projects' ? '进入项目工作区 →' : '打开关联项目 →' }}</RouterLink>
                  <button class="inline-button" type="button" @click.stop="selectRecord(record)">
                    {{ definition.key === 'projects' ? '编辑资料' : '编辑 →' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <PageState v-if="!filteredRecords.length" state="empty" title="暂无记录" :message="definition.key === 'projects' ? '接口已返回空列表，可点击上方按钮创建第一个完整项目。' : '接口已返回空列表，可点击上方按钮创建第一条记录。'" />
        </div>
        </template>
      </section>


    </div>

    <RecordFormDialog
      ref="recordDialog"
      :open="recordDialogOpen"
      :title="recordDialogTitle"
      :fields="formFields"
      :initial="form"
      :saving="saving || recordLoading"
      :submit-visible="writable && definition.key !== 'changes'"
      :submit-label="selected ? '保存修改' : `创建${definition.entityName}`"
      @close="recordDialogOpen = false; selected = null"
      @submit="save"
    >
      <!--
        五步排在最上面，0826 起也是变更**唯一**的填写口：通用字段区删了
        （和五步重复），新建、编辑都在五步里做。
        people 不传：这里是跨项目的全局清单，拿不到「本项目团队」，
        为了传而随便找一份人名会把别的项目的人列进责任人下拉。
      -->
      <template v-if="definition.key === 'changes'" #before-form>
        <ChangeStepBar
          :change-id="selected ? Number(selected.id) : null"
          :record="selected"
          :writable="changeStepWritable"
          :refresh-key="recordRefreshTick"
          :before-child-action="saveBeforeChildAction"
          :project-options="projectOptions"
          :creating="changeCreating"
          :cost-visible="selectedCostVisible"
          @changed="onChangeStepChanged"
          @void="voidChange"
          @create="createChangeFromStep"
        />
      </template>
      <template #operations>
        <RecordDrawer
          ref="drawer"
          :record="selected"
          :module="definition.module"
          :resource="definition.resource"
          :file-mode="drawerFileMode"
          :file-label="drawerFileMode === 'versions' ? 'VERSION CONTROL' : 'BUSINESS ATTACHMENTS'"
          :file-title="drawerFileMode === 'versions' ? '文件版本操作' : '业务附件'"
          :file-hint="drawerFileHint"
          :history-entity-type="historyEntityType"
          :before-child-action="saveBeforeChildAction"
          :children="drawerChildren"
          :status-actions="drawerStatusActions"
          :status-empty-hint="drawerStatusEmptyHint"
          status-label="STATUS OPERATION"
          status-title="状态操作"
          :writable="writable"
          readonly-hint="后端当前仅开放读取接口，本页不可提交修改。"
          :only-office="drawerFileMode === 'versions'"
          @status="changeStatus"
          @files-changed="load"
          @open-editor="openEditorHere"
        >
          <template #context>
            <p class="record-dialog-context">内部 ID：{{ selected?.id }}</p>
            <RouterLink v-if="selectedProjectPath" class="inline-button" :to="selectedProjectPath">打开关联项目工作区 →</RouterLink>
            <small v-if="!selectedProjectPath && projectScoped" class="field-error">尚未关联项目；请使用项目主数据的内部 ID 明确关联。</small>
          </template>

          <template #assist>
            <!-- 5Why/AI、一键铺 D0–D8：和项目页挂的是同一个组件。
                 变更的五步条不在这里了，搬到了字段之前的 before-form（0824 顺序调整） -->
            <IssueDrawerAssist
              v-if="definition.key === 'issues' && selected"
              :record="selected"
              :writable="writable"
              :get-values="drawerValues"
              :set-values="patchDrawerValues"
              @steps-changed="drawer?.reloadChildren()"
            />
          </template>
        </RecordDrawer>
      </template>
    </RecordFormDialog>

    <RecordFormDialog
      :open="maintenanceDialogOpen"
      :title="`${definition.entityName}模板批量维护`"
      :fields="[]"
      :initial="{}"
      :submit-visible="false"
      @close="maintenanceDialogOpen = false"
    >
      <section class="dialog-operations file-operations maintenance-operations">
        <span>TEMPLATE BATCH UPDATE</span><h3>模板批量维护</h3>
        <p class="operation-hint">批量维护表只有表头，用于成批新增或更新记录，不是业务表格模板。记录 ID 留空表示新增；填写内部 ID 表示更新。上传后只做预览，确认前不会写入。</p>
        <div class="button-row">
          <button class="button button-secondary" type="button" @click="downloadTemplate">下载批量维护表（CSV）</button>
        </div>
        <label><span>选择填写完成的模板</span><input ref="importInput" type="file" accept=".csv,text/csv" @change="importPreview = null" /></label>
        <button class="button button-secondary" type="button" :disabled="importLoading" @click="previewImport">{{ importLoading ? '正在校验…' : '上传并预览' }}</button>
        <div v-if="importPreview" class="import-preview">
          <b>{{ importPreview.fileName }}</b>
          <p>共 {{ importPreview.total }} 行 · 新增 {{ importPreview.creates }} · 更新 {{ importPreview.updates }} · 异常 {{ importPreview.invalid }}</p>
          <ul v-if="importPreview.invalid">
            <li v-for="row in importPreview.rows.filter((item) => item.errors.length).slice(0, 8)" :key="row.row">
              第 {{ row.row }} 行：{{ row.errors.join('；') }}
            </li>
          </ul>
          <button class="button button-primary button-block" type="button" :disabled="Boolean(importPreview.invalid) || importConfirming" @click="confirmImport">
            {{ importConfirming ? '正在写入…' : importPreview.invalid ? '请先修正异常行' : '确认写入' }}
          </button>
        </div>
      </section>
    </RecordFormDialog>

    <RecordFormDialog
      :open="creatingProject"
      title="新建项目"
      :fields="[]"
      :initial="{}"
      :submit-visible="false"
      @close="cancelProjectCreation"
    >
      <ProjectCreationWizard
        @cancel="cancelProjectCreation"
        @created="handleProjectCreated"
      />
    </RecordFormDialog>
  </template>
</template>

<style scoped>
/* 「下一步」提示条的样式跟着 ChangeDrawerAssist 一起走了：文案现在是每一步自带的 hint，
   由 ChangeStepBar 自己渲染、自己带样式（0824） */
.issue-8d-start { display: flex; align-items: center; gap: 10px; margin: 14px 0 6px; }
.issue-8d-start small { color: var(--muted); font-size: 12px; }
.issue-view-tabs { display: flex; gap: 12px; margin: 12px 0 2px; }
.issue-view-tabs .on { color: var(--ink); border-bottom: 2px solid var(--ink); font-weight: 800; }

.module-context {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  padding: 11px 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, .85);
}

.module-context .module-code {
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--blue);
  background: var(--paper);
  font: 800 17px var(--display);
}

.module-context > div:nth-child(2) {
  display: grid;
  gap: 5px;
}

.module-context > div:nth-child(2) > span {
  color: var(--blue);
  /* 11px 下限：这些眉标里混着中文（「当前清单」「我的项目」），
     中文字形在 9px 基本糊成一团，拉丁小型大写字母还能勉强读 */
  font: 800 11px var(--display);
  letter-spacing: .12em;
}

.module-context p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.45;
}

.workbench-grid-full {
  grid-template-columns: minmax(0, 1fr);
}

.record-hint {
  margin: -4px 0 14px;
  padding: 9px 11px;
  border-left: 3px solid var(--blue);
  color: var(--muted);
  background: rgba(36, 194, 184, .05);
  font-size: 12px;
}

.record-panel .data-table td:last-child {
  white-space: nowrap;
}

.record-panel .data-table td:last-child .inline-button + .inline-button {
  margin-left: 10px;
}

.record-panel .data-table .project-row {
  cursor: pointer;
}

.record-panel .data-table .project-row:hover,
.record-panel .data-table .project-row:focus {
  outline: 2px solid var(--blue);
  outline-offset: -2px;
  background: rgba(18, 109, 166, .06);
}

/*
  结束态（已归档 / 已删除）压暗（0916）。
  它们和在跑的项目原来长得一模一样，一屏 20 行里有 12 行是这种——
  排序已经把它们沉到底，再压一层暗，扫一眼就知道下面那截不用看。
  hover 时恢复正常：还是能点进去看的，只是不抢眼。
*/
.record-panel .data-table tr.stage-archived td,
.record-panel .data-table tr.stage-deleted td {
  color: var(--muted);
  background: rgba(15, 23, 36, .018);
}
.record-panel .data-table tr.stage-archived .project-name-link,
.record-panel .data-table tr.stage-deleted .project-name-link { color: var(--muted); font-weight: 700; }
.record-panel .data-table tr.stage-archived:hover td,
.record-panel .data-table tr.stage-deleted:hover td { color: var(--ink); }

/* 草稿行：不压暗（它要人去启动），但编号列留一条竖杠标出来 */
.record-panel .data-table tr.stage-not-started td:first-child {
  box-shadow: inset 3px 0 rgba(74, 91, 122, .45);
}

.project-filters { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0 0; }
.project-filter {
  padding: 5px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--white);
  color: var(--muted);
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.project-filter b { margin-left: 5px; color: var(--ink); font-size: 13px; }
.project-filter:hover { border-color: var(--blue); color: var(--blue); }
.project-filter.is-active { border-color: var(--blue); background: rgba(18, 109, 166, .09); color: var(--blue); }
.project-filter.is-active b { color: var(--blue); }

.project-name-link {
  display: grid;
  gap: 3px;
  color: var(--ink);
  font-weight: 800;
  text-decoration: none;
}

.project-name-link span {
  color: var(--blue);
  font-size: 10px;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.project-open-button {
  display: inline-block;
  padding: 7px 9px;
  border: 1px solid var(--blue);
  color: white;
  background: var(--blue);
  text-decoration: none;
}

@media (max-width: 600px) {
  .module-context {
    grid-template-columns: 48px 1fr;
  }

  .module-context .module-code {
    width: 48px;
    height: 48px;
    font-size: 14px;
  }

  .module-context > .button {
    grid-column: 1 / -1;
  }
}
</style>
